import { useState } from 'react'
import { useAuthContext } from '@/lib/auth-context'
import { Section, useBearer, useToast } from './_admin-utils'

const ROLE_OPTIONS = [
  { value: 'teacher', label: 'Teacher' },
  { value: 'sponsor', label: 'Sponsor' },
  { value: 'coach', label: 'Coach' },
  { value: 'guardian', label: 'Guardian' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'admin', label: 'Admin' },
  { value: 'superadmin', label: 'Superadmin' },
]

const SEND_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'magic', label: 'Magic Link' },
  { value: 'reset', label: 'Password Reset' },
]

export default function CreateUser() {
  const { profile } = useAuthContext()
  const bearer = useBearer()
  const { show, ToastHost } = useToast()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [role, setRole] = useState('teacher')
  const [addToAllowlist, setAddToAllowlist] = useState(true)
  const [sendOption, setSendOption] = useState('magic')
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<Record<string, unknown> | null>(null)
  const [error, setError] = useState<string | null>(null)

  const isAdmin = ['admin', 'superadmin'].includes(profile?.role ?? '')
  if (!isAdmin) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-cocoa/70">You do not have permission to view this page.</div>
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!bearer) {
      setError('Not authenticated')
      return
    }
    setSubmitting(true)
    setError(null)
    setResult(null)
    try {
      const response = await fetch('/api/v1/admin/users/create', {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...bearer,
        },
        body: JSON.stringify({
          email,
          name,
          role,
          addToAllowlist,
          send: sendOption === 'none' ? null : sendOption,
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data?.error || 'Unable to create user')
      }
      setResult(data)
      show('User processed')
    } catch (err: unknown) {
      setError((err as Error | null)?.message || 'Unable to create user')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-6">
      <div>
        <a href="/admin" className="text-sm underline text-cocoa/70 hover:text-cocoa">
          ← Back to Admin
        </a>
      </div>
      <header className="space-y-2">
        <h1 className="text-3xl font-semibold text-cocoa">Create User</h1>
        <p className="text-sm text-cocoa/70">
          Create an account, assign a role, ensure allowlist access, and optionally send onboarding links in one step.
        </p>
      </header>

      <Section title="New user" actions={null}>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-cocoa/80">
              <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              />
            </label>
            <label className="space-y-1 text-sm text-cocoa/80">
              <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Full name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              />
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1 text-sm text-cocoa/80">
              <span className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Role</span>
              <select
                value={role}
                onChange={(event) => setRole(event.target.value)}
                className="w-full rounded-lg border border-white/70 bg-shell/40 px-3 py-2 text-sm text-cocoa focus:border-terracotta focus:outline-none"
              >
                {ROLE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <fieldset className="space-y-2 text-sm text-cocoa/80">
              <legend className="block text-xs font-semibold uppercase tracking-wide text-cocoa/60">Send link</legend>
              <div className="grid gap-2">
                {SEND_OPTIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="send-option"
                      value={option.value}
                      checked={sendOption === option.value}
                      onChange={(event) => setSendOption(event.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <label className="flex items-center gap-2 text-sm text-cocoa/80">
            <input
              type="checkbox"
              checked={addToAllowlist}
              onChange={(event) => setAddToAllowlist(event.target.checked)}
            />
            <span>Add to allowlist</span>
          </label>

          {error ? <div className="text-sm text-red-600">{error}</div> : null}

          <button
            type="submit"
            disabled={submitting}
            className="rounded-lg border border-shell bg-white px-4 py-2 text-sm font-semibold text-cocoa hover:bg-shell/40 disabled:opacity-60"
          >
            {submitting ? 'Processing…' : 'Create user'}
          </button>
        </form>
      </Section>

      {result ? (
        <Section title="Summary" actions={null}>
          <dl className="grid grid-cols-1 gap-2 text-sm text-cocoa/80 sm:grid-cols-2">
            <div>
              <dt className="uppercase text-xs tracking-wide text-cocoa/60">Auth user created</dt>
              <dd className="font-semibold text-cocoa">{result.created ? 'Yes' : 'Existing'}</dd>
            </div>
            <div>
              <dt className="uppercase text-xs tracking-wide text-cocoa/60">Profile updated</dt>
              <dd className="font-semibold text-cocoa">{result.updatedProfile ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt className="uppercase text-xs tracking-wide text-cocoa/60">Allowlist</dt>
              <dd className="font-semibold text-cocoa">{result.allowlisted ? 'Added / active' : 'Skipped'}</dd>
            </div>
            <div>
              <dt className="uppercase text-xs tracking-wide text-cocoa/60">Link sent</dt>
              <dd className="font-semibold text-cocoa">{result.sent ? (result.sent === 'magic' ? 'Magic link' : 'Reset') : 'Not sent'}</dd>
            </div>
          </dl>
        </Section>
      ) : null}

      <ToastHost />
    </div>
  )
}
